import React, { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { shopAdminApi, ShopApiError } from "./shopAdminApi";
import WebshopKustomRetry from "./WebshopKustomRetry";
import { jobStates, stateLabels, reasonLabels, type JobFilter, type InspectionAccount, type KustomJobPage,
  type KustomJobSummary, type KustomJobDetail } from "./kustomInspectionModel";

const accountText = (account: InspectionAccount | null) => account ?
  `${account.kustomEnvironment === "playground" ? "Playground (test)" : "Produktion (live)"} · MID ${account.merchantId}` : "Okänd miljö/MID";
const dateText = (value: string | null) => value ? new Date(value).toLocaleString("sv-SE", { timeZone: "Europe/Stockholm" }) : "Okänt";
const amountText = (value: number) => new Intl.NumberFormat("sv-SE", { style: "currency", currency: "SEK" }).format(value / 100);
const errorText = (error: unknown) => error instanceof ShopApiError && [401, 403].includes(error.status) ?
  "Behörighet till Kustom-jobben saknas eller har återkallats. Logga in igen eller kontrollera rollen." :
  "Kustom-jobben kunde inte hämtas. Kontrollera API-anslutningen och försök igen.";

function JobFacts({ job }: { job: KustomJobSummary }) {
  return <div className="WebshopCaseDetail">
    <p>Händelse: {job.eventType} · {stateLabels[job.state]}</p>
    <p>Sparat konto: {accountText(job.account)}</p>
    {job.matchesActiveAccount === false && <Alert severity="warning">Sparat konto skiljer sig från aktivt konto. Jobbet får inte behandlas med fel nyckel.</Alert>}
    {job.matchesActiveAccount === null && <Alert severity="warning">Aktiv kontokoppling kan inte jämföras. Detta bekräftar inte API-åtkomst.</Alert>}
    {job.reasonCode && <Alert severity={job.state === "quarantined" ? "warning" : "info"}>{reasonLabels[job.reasonCode]}</Alert>}
    {job.integrityIssues.length > 0 && <Alert severity="warning">Ofullständiga eller ogiltiga jobbuppgifter: {job.integrityIssues.join(", ")}. Utred serverposterna; visningen reparerar dem inte.</Alert>}
    <p>Avläsningsförsök i aktuell omgång: {job.attempts ?? "okänt"} / 8</p>
    <p>Mottaget i kön: {dateText(job.createdAt)} · Uppdaterat: {dateText(job.updatedAt)}</p>
    {["pending", "retry", "leased"].includes(job.state) && <p>Nästa kontroll: {dateText(job.nextCheckAt)}
      {job.state === "leased" && ` · Avläsningslås till ${dateText(job.leaseUntil)}`}</p>}
  </div>;
}
function JobDetail({ user, jobId, onClose, onRequested }: { user: User; jobId: string; onClose: () => void; onRequested: () => void }) {
  const [detail, setDetail] = useState<KustomJobDetail | null>(null), [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [retryOpen, setRetryOpen] = useState(false);
  useEffect(() => {
    let live = true; setDetail(null); setError("");
    shopAdminApi.kustomJob(user, jobId).then((value) => { if (live) setDetail(value); })
      .catch((failure) => { if (live) setError(errorText(failure)); });
    return () => { live = false; };
  }, [user, jobId, refresh]);
  if (retryOpen) return <WebshopKustomRetry user={user} jobId={jobId} onClose={() => setRetryOpen(false)} onRequested={onRequested} />;
  return <Dialog open fullWidth maxWidth="sm" onClose={onClose} aria-labelledby="kustom-job-title">
    <DialogTitle id="kustom-job-title">Kustom-jobb</DialogTitle>
    <DialogContent className="WebshopOfferDialog WebshopKustomRecovery">
      <p>Jobbreferens: {jobId}</p>
      <Alert severity="info">Avläsningen är underlag för utredning. Slutlig betalnings- och leveransvalidering återstår; lager och order ändras inte här.</Alert>
      {error && <Alert severity="error">{error}</Alert>}
      {!error && !detail && <p role="status">Hämtar aktuellt jobb…</p>}
      {detail && <>
        <p>Aktivt konto: {accountText(detail.activeAccount)} · Avläst {dateText(detail.asOf)}</p>
        <JobFacts job={detail.job} />
        <dl className="WebshopContentFields">{Object.entries(detail.resources).filter(([, value]) => value !== null).map(([field, value]) =>
          <div key={field}><dt>{{ orderId: "Kustom-order", captureId: "Kustom-capture", refundId: "Kustom-återbetalning", disputeId: "Kustom-tvist" }[field]}</dt><dd>{value}</dd></div>)}</dl>
        {detail.observation ? <section aria-label="Observerat Kustom-svar">
          <h4>Observerat Kustom-svar</h4>
          <p>Observerat {dateText(detail.observation.observedAt)} · SE / SEK</p>
          <p>Providerstatus: {detail.observation.status} · Bedrägeristatus: {detail.observation.fraudStatus}</p>
          <p>Orderbelopp: {amountText(detail.observation.orderAmountOre)}</p>
          <p>Observerat fångat belopp: {amountText(detail.observation.capturedAmountOre)} · Observerat återbetalt: {amountText(detail.observation.refundedAmountOre)}</p>
          <Alert severity="warning">Detta är providerobservationer. De bekräftar inte accepterad betalning, genomförd lokal återbetalning eller tillstånd att skicka varor.</Alert>
        </section> : <p>Ingen giltig providerobservation visas.</p>}
      </>}
    </DialogContent>
    <DialogActions className="WebshopContentActions"><Button onClick={onClose}>Stäng jobb</Button>
      {detail?.job.state === "quarantined" && <Button onClick={() => setRetryOpen(true)}>Begär ny avläsning</Button>}
      <Button onClick={() => setRefresh((value) => value + 1)}>Uppdatera jobb</Button></DialogActions>
  </Dialog>;
}

export default function WebshopKustomRecovery({ user }: { user: User }) {
  const [filter, setFilter] = useState<JobFilter>("quarantined"), [cursor, setCursor] = useState<string | undefined>();
  const [page, setPage] = useState<KustomJobPage | null>(null), [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0), [selectedId, setSelectedId] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  useEffect(() => {
    let live = true; setPage(null); setError(""); setSelectedId(null);
    shopAdminApi.kustomJobs(user, filter, cursor).then((value) => { if (live) setPage(value); })
      .catch((failure) => { if (live) setError(errorText(failure)); });
    return () => { live = false; };
  }, [user, filter, cursor, refresh]);
  const restart = () => { setCursor(undefined); setRefresh((value) => value + 1); };
  return <section className="WebshopSettingsCard WebshopSettingsBody WebshopKustomRecovery" aria-labelledby="kustom-recovery-title">
    <h3 id="kustom-recovery-title">Kustom — avläsning och utredning</h3>
    <p>Skyddad jobbkö med högst 25 poster per sida. Sorteras efter jobbreferens, inte ålder eller prioritet. Status kan ändras mellan sidor; uppdatera för en ny genomgång.</p>
    <Alert severity="info">Vyn ändrar inte betalning eller lager. Behörig personal kan begära en ny avläsningsomgång för vissa utredda jobb.
      Godkända köp, återbetalningar och tvister behöver separata skyddade arbetsflöden.</Alert>
    {notice && <Alert severity="success" role="status">{notice}</Alert>}
    <div className="WebshopKustomControls">
      <TextField select label="Kustom-jobbstatus" value={filter} onChange={(event) => { setFilter(event.target.value as JobFilter); setCursor(undefined); }}>
        {[...jobStates, "all" as const].map((state) => <MenuItem key={state} value={state}>{stateLabels[state]}</MenuItem>)}
      </TextField><Button onClick={restart}>Uppdatera jobbkön</Button>
    </div>
    {error && <Alert severity="error">{error}</Alert>}
    {!error && !page && <p role="status">Hämtar Kustom-jobb…</p>}
    {page && <>
      <p>Aktivt konto: {accountText(page.activeAccount)} · Avläst {dateText(page.asOf)}</p>
      {page.jobs.length === 0 && <p>Inga jobb på denna sida för vald status.</p>}
      <div className="WebshopCatalogList">{page.jobs.map((job) => <article className="WebshopCatalogProduct" key={job.jobId}>
        <p>Jobbreferens: {job.jobId}</p><JobFacts job={job} />
        <Button onClick={() => setSelectedId(job.jobId)} aria-label={`Granska Kustom-jobb ${job.jobId}`}>Granska jobb</Button>
      </article>)}</div>
      <div className="WebshopKustomControls">{cursor && <Button onClick={restart}>Till första sidan</Button>}
        {page.nextCursor && <Button onClick={() => setCursor(page.nextCursor!)}>Nästa jobbsida</Button>}</div>
    </>}
    {selectedId && <JobDetail key={`${user.uid}:${selectedId}`} user={user} jobId={selectedId} onClose={() => setSelectedId(null)}
      onRequested={() => { setSelectedId(null); setNotice("Ny avläsning begärd. Resultatet visas efter fortsatt behandling; köpstatus är inte bekräftad."); restart(); }} />}
  </section>;
}
